<script setup lang="ts">
import { type ComputedRef, computed, nextTick, onMounted, ref } from 'vue';
import { FhButton, FhIconButton } from '@fareharbor-com/beacon-vue';
import { download, filter } from '@fareharbor-com/beacon-vue/icons';
import { initFlowbite, initTooltips } from 'flowbite';
import { Logger } from '@/common/Logger';
import { type GetItemsCSVResponse, getItemsCSV } from '@/common/api/getItems';
import {
  DEFAULT_OFFERED_SINCE_VARIANT,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT_BY,
  PAGE_SIZE_OPTIONS,
  QueryParamName,
  SortByOption,
} from '@/common/constants/items';
import { saveFile } from '@/common/files';
import { makeCSVFilename } from '@/common/makeCSVFilename';
import { parsePositiveInt } from '@/common/parsePositiveInt';
import { transformItemsToTableData } from '@/common/transformItems';
import SearchBars from '@/components/SearchBar/SearchBars.vue';
import ItemListCards from '@/components/itemList/ItemListCards.vue';
import ItemListFilters from '@/components/itemList/ItemListFilters.vue';
import ItemListFooter from '@/components/itemList/ItemListFooter.vue';
import ItemListTable from '@/components/itemList/ItemListTable.vue';
import { OfferedSinceOptionVariant } from '@/components/itemList/types';
import { type QueryParams, useQueryParams } from '@/composables/queryParams';
import { useTracking } from '@/composables/useTracking';
import { useItemFiltersStore } from '@/stores/itemFilters';
import { useItemsStore } from '@/stores/items';
import { useSearchStore } from '@/stores/search';

const itemsStore = useItemsStore();
const searchStore = useSearchStore();
const itemFiltersStore = useItemFiltersStore();

const { getAllQueryParams, updateMultipleQueryParams } = useQueryParams();

const showFilters = ref(true);
// Simple responsive default: Grid for small screens, Table for larger screens
const isGridView = ref(window.innerWidth < 768);

function toggleFilters() {
  showFilters.value = !showFilters.value;

  if (showFilters.value) {
    // once the filters are back in the DOM, wire up all Flowbite JS again
    nextTick().then(() => {
      initFlowbite();
      initTooltips();
    });
  }
}

function setTableView() {
  isGridView.value = false;
}

function setGridView() {
  isGridView.value = true;
}

const currentPage = ref(DEFAULT_PAGE);
const currentPageSize = ref(DEFAULT_PAGE_SIZE);
const currentSortBy = ref(DEFAULT_SORT_BY);

// @ts-ignore
// computed properties
const itemsTableData: ComputedRef<any> = computed(() => {
  const items = itemsStore.itemsByPage(currentPage.value);
  return transformItemsToTableData(items);
});

const pageStart: ComputedRef<number> = computed(
  () => (currentPage.value - 1) * currentPageSize.value + 1,
);

const pageEnd: ComputedRef<number> = computed(() => {
  const calculatedPageEnd = pageStart.value + currentPageSize.value - 1;
  if (calculatedPageEnd > itemsStore.totalCount) return itemsStore.totalCount;
  return calculatedPageEnd;
});

const totalPagesCount: ComputedRef<number> = computed(() =>
  Math.ceil(itemsStore.totalCount / currentPageSize.value),
);

const tracking = useTracking();

const CSV_FILENAME = {
  MARKETPLACE: 'marketplace-items',
  MARKETPLACE_BOOKMARKED: 'marketplace-bookmarked-items',
} as const;

function setCurrentPage(page: string | null) {
  if (page) {
    currentPage.value = Number.parseInt(page, 10);
    if (itemsStore.totalCount === 0) {
      currentPage.value = DEFAULT_PAGE;
    } else {
      const lastPage = Math.ceil(itemsStore.totalCount / currentPageSize.value);
      if (currentPageSize.value !== null && currentPage.value > lastPage) {
        currentPage.value = lastPage;

        // Update the URL to reflect the corrected page
        const url = new URL(window.location.href);
        url.searchParams.set(QueryParamName.PAGE, lastPage.toString());
        window.location.href = url.toString(); // Redirect to corrected URL
      }
    }
  } else {
    currentPage.value = DEFAULT_PAGE;
  }
}

function readQueryParams() {
  const params = getAllQueryParams();

  const pageSizeParam = params[QueryParamName.PAGE_SIZE] || null;
  const parsedPageSize = parsePositiveInt({
    value: pageSizeParam,
    fallback: DEFAULT_PAGE_SIZE,
    min: 1,
    max: 100,
  });

  if (
    parsedPageSize === DEFAULT_PAGE_SIZE &&
    pageSizeParam &&
    pageSizeParam !== DEFAULT_PAGE_SIZE.toString()
  ) {
    const url = new URL(window.location.href);
    url.searchParams.delete(QueryParamName.PAGE_SIZE);
    window.history.replaceState({}, '', url.toString());
  }
  currentPageSize.value = parsedPageSize;

  const pageParam = params[QueryParamName.PAGE] || null;
  const parsedPage = parsePositiveInt({
    value: pageParam,
    fallback: DEFAULT_PAGE,
    min: 1,
  });
  if (
    parsedPage === DEFAULT_PAGE &&
    pageParam &&
    pageParam !== DEFAULT_PAGE.toString()
  ) {
    const url = new URL(window.location.href);
    url.searchParams.delete(QueryParamName.PAGE);
    window.history.replaceState({}, '', url.toString());
  }

  setCurrentPage(parsedPage.toString());

  const sortByParam = params[QueryParamName.SORT_BY] || null;
  currentSortBy.value = sortByParam
    ? Number.parseInt(sortByParam, 10)
    : DEFAULT_SORT_BY;

  const raw = params[QueryParamName.OFFERED_SINCE];
  const parsed = raw ? Number.parseInt(raw, 10) : DEFAULT_OFFERED_SINCE_VARIANT;

  itemFiltersStore.offeredSinceVariant = Number.isNaN(parsed)
    ? DEFAULT_OFFERED_SINCE_VARIANT
    : parsed;

  const tags = params[QueryParamName.TAGS];
  itemFiltersStore.tags = tags ? new Set(tags.split(',')) : new Set();

  const currencies = params[QueryParamName.CURRENCIES];
  itemFiltersStore.currencies = currencies
    ? new Set(currencies.split(','))
    : new Set();

  const locationSearch = params[QueryParamName.LOC_SEARCH];
  searchStore.locationSearchText = locationSearch || '';

  // Extract coordinate parameters from URL if they exist
  const neLat = params.ne_lat == null ? null : params.ne_lat;
  const neLng = params.ne_lng == null ? null : params.ne_lng;
  const swLat = params.sw_lat == null ? null : params.sw_lat;
  const swLng = params.sw_lng == null ? null : params.sw_lng;

  // Set coordinate values in search store
  searchStore.ne_lat = neLat ? parseFloat(neLat) : null;
  searchStore.ne_lng = neLng ? parseFloat(neLng) : null;
  searchStore.sw_lat = swLat ? parseFloat(swLat) : null;
  searchStore.sw_lng = swLng ? parseFloat(swLng) : null;

  // Set coordinate change flag if coordinates are present
  searchStore.locationSearchCoordinatesChanged = !!(
    neLat &&
    neLng &&
    swLat &&
    swLng
  );

  const search = params[QueryParamName.SEARCH];
  searchStore.companyItemSearchText = search || '';

  const isBookmarkedParam = params[QueryParamName.IS_BOOKMARKED];
  itemFiltersStore.isBookmarked = isBookmarkedParam === 'true';

  Logger.debug('Local and store variables updated from query params');
}

function updateQueryParams() {
  const page =
    currentPage.value !== DEFAULT_PAGE ? currentPage.value.toString() : null;
  const pageSize =
    currentPageSize.value !== DEFAULT_PAGE_SIZE
      ? currentPageSize.value.toString()
      : null;
  const sortBy =
    currentSortBy.value !== DEFAULT_SORT_BY
      ? currentSortBy.value.toString()
      : null;

  const offeredSinceVariant =
    itemFiltersStore.offeredSinceVariant !==
    OfferedSinceOptionVariant.NO_PREFERENCE
      ? itemFiltersStore.offeredSinceVariant
      : null;
  const tags =
    itemFiltersStore.tags.size > 0
      ? Array.from(itemFiltersStore.tags).join(',')
      : null;
  const currencies =
    itemFiltersStore.currencies.size > 0
      ? Array.from(itemFiltersStore.currencies).join(',')
      : null;
  const isBookmarked = itemFiltersStore.isBookmarked === true ? 'true' : null;

  // Include coordinate parameters if they exist
  const neLat = searchStore.ne_lat !== null ? String(searchStore.ne_lat) : null;
  const neLng = searchStore.ne_lng !== null ? String(searchStore.ne_lng) : null;
  const swLat = searchStore.sw_lat !== null ? String(searchStore.sw_lat) : null;
  const swLng = searchStore.sw_lng !== null ? String(searchStore.sw_lng) : null;

  const params: QueryParams = {
    [QueryParamName.PAGE]: page,
    [QueryParamName.PAGE_SIZE]: pageSize,
    [QueryParamName.SORT_BY]: sortBy,
    [QueryParamName.OFFERED_SINCE]: offeredSinceVariant as string | null,
    [QueryParamName.TAGS]: tags,
    [QueryParamName.CURRENCIES]: currencies,
    [QueryParamName.LOC_SEARCH]: searchStore.locationSearch,
    [QueryParamName.SEARCH]: searchStore.companyItemSearch,
    [QueryParamName.IS_BOOKMARKED]: isBookmarked,
    ne_lat: neLat,
    ne_lng: neLng,
    sw_lat: swLat,
    sw_lng: swLng,
  };

  updateMultipleQueryParams(params);
  Logger.debug(`Query params updated with local and store variables ${params}`);
}

function loadItems() {
  itemsStore.loadPageItems({
    page: currentPage.value,
    pageSize: currentPageSize.value,
    sortBy: currentSortBy.value,
  });

  window.scrollTo({ top: 0, behavior: 'smooth' });
  Logger.debug('Items loaded');
}

function handlePageChange(newPageNumber: number) {
  currentPage.value = newPageNumber;
  updateQueryParams();
}

function handleSortByChange(newOption: SortByOption) {
  Logger.debug(`New sort option selected: ${newOption}`);
  currentSortBy.value = newOption;
  currentPage.value = DEFAULT_PAGE;
  updateQueryParams();
}

function handlePageSizeChange(newPageSize: number) {
  Logger.debug(`Page size changed to: ${newPageSize}`);
  currentPageSize.value = newPageSize;
  currentPage.value = DEFAULT_PAGE;
  updateQueryParams();
}

async function handleFiltersChange() {
  Logger.debug('Filters changed!');
  currentPage.value = DEFAULT_PAGE;
  updateQueryParams();
}

function handleSearchTextChange() {
  if (
    !searchStore.locationSearchChanged &&
    !searchStore.companyItemSearchChanged &&
    !searchStore.locationSearchCoordinatesChanged
  ) {
    return;
  }
  currentPage.value = DEFAULT_PAGE;
  updateQueryParams();
}
searchStore.$subscribe(handleSearchTextChange);

async function downloadItemsCSV() {
  const response: GetItemsCSVResponse = await getItemsCSV({
    page: currentPage.value,
    pageSize: currentPageSize.value,
    sortBy: currentSortBy.value,
    tags: itemFiltersStore.tags,
    offeredSince: itemFiltersStore.offeredSince,
    currencies: itemFiltersStore.currencies,
    searchLocation: searchStore.locationSearch,
    search: searchStore.companyItemSearch,
    isBookmarked: itemFiltersStore.isBookmarked,
  });

  if (response.error !== null) {
    Logger.info(`[downloadCSV] Could not get CSV ${response.error}`);
    throw response.error;
  }

  if (response.data !== undefined) {
    const filenamePrefix = itemFiltersStore.isBookmarked
      ? CSV_FILENAME.MARKETPLACE_BOOKMARKED
      : CSV_FILENAME.MARKETPLACE;
    saveFile(response.data, makeCSVFilename(filenamePrefix));
  }
}

itemsStore.$onAction(({ name, after }) => {
  after(() => {
    Logger.debug(`[ItemListView][itemsStore][$onAction] ${name}`);
    if (name === 'loadPageItems') {
      Logger.debug(
        `[ItemListView][itemsStore][$onAction]
        AFTER
        ${name}
        -> initialize tooltips,
      `,
      );
      nextTick().then(() => {
        initTooltips();
      });
    }
  });
});

onMounted(async () => {
  readQueryParams();
  tracking?.trackViewItemList();
  itemsStore.isLoading = true;
  itemsStore.clear();
  await nextTick();
  loadItems();
  initFlowbite();
  initTooltips();
});
</script>

<template>
  <main class="item-list-view overflow-hidden relative flex-1">
    <div class="px-4 py-4 sm:px-5 sm:py-5 mx-auto">
      <div
        class="flex flex-col md:flex-row items-start md:items-center gap-4 w-full"
      >
        <div class="w-full md:w-1/2">
          <SearchBars />
        </div>
        <div class="flex flex-row gap-4 w-full md:w-1/2 justify-end">
          <FhButton
            variant="tertiary"
            class="p-0 flex-1 md:flex-none"
            size="md"
            data-test-id="show-filters-button"
            :icon="filter"
            @click="toggleFilters"
          >
            {{ showFilters ? 'Hide Filters' : 'Filters' }}
          </FhButton>
          <!-- Grid/Table Toggle -->
          <div class="flex bg-gray-100 rounded-lg p-2 gap-1">
            <FhButton
              :variant="!isGridView ? 'primary' : 'ghost'"
              size="sm"
              @click="setTableView"
              data-test-id="table-view-button"
            >
              List
            </FhButton>
            <FhButton
              :variant="isGridView ? 'primary' : 'ghost'"
              size="sm"
              @click="setGridView"
              data-test-id="grid-view-button"
            >
              Grid
            </FhButton>
          </div>
          <FhIconButton
            :icon="download"
            size="md"
            @click="downloadItemsCSV"
            data-test-id="download-csv-button"
            class="flex-1 md:flex-none"
          >
            Download CSV
          </FhIconButton>
        </div>
      </div>
      <transition name="slide-fade">
        <ItemListFilters
          v-if="showFilters"
          @filtersChanged="handleFiltersChange"
          @sortByChanged="handleSortByChange"
          :currentSortBy="currentSortBy"
          class="mt-4 mb-4 sm:mb-6"
        />
      </transition>

      <ItemListTable
        v-if="!isGridView"
        :data="itemsTableData"
        :isLoading="itemsStore.isLoading"
      />
      <ItemListCards
        v-if="isGridView"
        :data="itemsTableData"
        :isLoading="itemsStore.isLoading"
      />
      <ItemListFooter
        class="mt-3 w-full"
        :showPagination="!itemsStore.isLoading && itemsTableData.length > 0"
        :showCount="!itemsStore.isLoading && itemsTableData.length > 0"
        :pageStart="pageStart"
        :pageEnd="pageEnd"
        :totalItemsCount="itemsStore.totalCount"
        :totalPagesCount="totalPagesCount"
        :currentPage="currentPage"
        :currentPageSize="currentPageSize"
        :pageSizeOptions="PAGE_SIZE_OPTIONS"
        @pageChanged="handlePageChange"
        @pageSizeChanged="handlePageSizeChange"
        dataTestId="footer-section"
      />
    </div>
  </main>
</template>
<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
